import React, { useState, useRef, useEffect } from 'react';
import { deriveProfileName, profileSubtitle, profileInitials } from '../services/profiles';
import './ProfileSwitcher.css';

/**
 * Header dropdown for switching between saved birth charts.
 * Purely presentational — all mutations are handed up to App.
 */
function ProfileSwitcher({
  onFeedback,
  profiles,
  activeId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const rootRef = useRef(null);
  const renameInputRef = useRef(null);

  const active = profiles.find((p) => p.id === activeId) || profiles[0];

  // Close on outside click / Escape so the menu never traps the user.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setRenamingId(null);
        setConfirmingDeleteId(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setRenamingId(null);
        setConfirmingDeleteId(null);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const startRename = (profile, e) => {
    e.stopPropagation();
    setConfirmingDeleteId(null);
    setRenamingId(profile.id);
    setDraftName(deriveProfileName(profile));
  };

  const commitRename = () => {
    if (renamingId) onRename(renamingId, draftName);
    setRenamingId(null);
    setDraftName('');
  };

  const handleSelect = (id) => {
    if (renamingId) return;
    onSelect(id);
    setOpen(false);
    setConfirmingDeleteId(null);
  };

  if (!active) return null;

  return (
    <div className="profile-switcher" ref={rootRef}>
      <button
        type="button"
        className={`switcher-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="switcher-avatar" aria-hidden="true">
          {profileInitials(active)}
        </span>
        <span className="switcher-labels">
          <span className="switcher-name">{deriveProfileName(active)}</span>
          <span className="switcher-sub">{profileSubtitle(active)}</span>
        </span>
        <svg className="switcher-caret" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="switcher-menu" role="listbox">
          <div className="switcher-menu-head">Charts</div>

          <ul className="switcher-list">
            {profiles.map((profile) => {
              const isActive = profile.id === activeId;
              const isRenaming = renamingId === profile.id;
              const isConfirming = confirmingDeleteId === profile.id;

              return (
                <li
                  key={profile.id}
                  className={`switcher-item ${isActive ? 'is-active' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(profile.id)}
                >
                  <span className="item-avatar" aria-hidden="true">
                    {profileInitials(profile)}
                  </span>

                  <span className="item-body">
                    {isRenaming ? (
                      <input
                        ref={renameInputRef}
                        className="item-rename-input"
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') {
                            setRenamingId(null);
                            setDraftName('');
                          }
                        }}
                        maxLength={40}
                      />
                    ) : (
                      <>
                        <span className="item-name">{deriveProfileName(profile)}</span>
                        <span className="item-sub">{profileSubtitle(profile)}</span>
                      </>
                    )}
                  </span>

                  {!isRenaming && (
                    <span className="item-actions">
                      {isConfirming ? (
                        <>
                          <button
                            type="button"
                            className="item-action danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(profile.id);
                              setConfirmingDeleteId(null);
                            }}
                          >
                            Delete
                          </button>
                          <button
                            type="button"
                            className="item-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmingDeleteId(null);
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="item-icon-btn"
                            title="Rename"
                            aria-label={`Rename ${deriveProfileName(profile)}`}
                            onClick={(e) => startRename(profile, e)}
                          >
                            <svg viewBox="0 0 16 16" aria-hidden="true">
                              <path d="M11.5 2.5l2 2L6 12l-2.5.5L4 10l7.5-7.5z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                            </svg>
                          </button>
                          {profiles.length > 1 && (
                            <button
                              type="button"
                              className="item-icon-btn"
                              title="Delete"
                              aria-label={`Delete ${deriveProfileName(profile)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingId(null);
                                setConfirmingDeleteId(profile.id);
                              }}
                            >
                              <svg viewBox="0 0 16 16" aria-hidden="true">
                                <path d="M3 4.5h10M6.5 4.5V3h3v1.5M5 4.5l.5 8h5l.5-8" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            className="switcher-add"
            onClick={() => {
              onAdd();
              setOpen(false);
            }}
          >
            <span className="add-plus" aria-hidden="true">+</span>
            Add another chart
          </button>

          <button
            type="button"
            className="switcher-link"
            onClick={() => {
              onFeedback();
              setOpen(false);
            }}
          >
            <span className="link-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16">
                <path
                  d="M2.5 3.5h11v9h-11zM2.5 3.5l5.5 4.5 5.5-4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Send feedback
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfileSwitcher;
